import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { UserCard } from '../types/pokemon';
import { useAuth } from './useAuth';

export function useCollection() {
  const [collection, setCollection] = useState<Record<number, UserCard>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    if (user) {
      fetchCollection();
    }
  }, [user]);

  const fetchCollection = async () => {
    try {
      setLoading(true);
      const allCards: UserCard[] = [];
      let page = 0;
      const pageSize = 1000; // Utiliser la limite max de Supabase
      let hasMoreData = true;

      // Récupérer toutes les cartes avec pagination
      while (hasMoreData) {
        const { data, error } = await supabase
          .from('user_cards')
          .select('card_id, quantity, wanted, tradable')
          .eq('user_id', user?.id)
          .range(page * pageSize, (page + 1) * pageSize - 1);

        if (error) throw error;

        if (data && data.length > 0) {
          allCards.push(...data);
          hasMoreData = data.length === pageSize; // S'il y a exactement pageSize éléments, il peut y en avoir plus
          page++;
        } else {
          hasMoreData = false;
        }
      }

      // Convertir en map pour un accès rapide
      const collectionMap = allCards.reduce((acc, card) => {
        acc[card.card_id] = card;
        return acc;
      }, {} as Record<number, UserCard>);
      
      setCollection(collectionMap);
    } catch (err) {
      console.error('Error fetching collection:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch collection');
    } finally {
      setLoading(false);
    }
  };

  const updateCardQuantity = async (cardId: number, quantity: number) => {
    if (!user) {
      console.error('User must be authenticated to update collection');
      return;
    }

    try {
      if (quantity <= 0) {
        // Delete the card from collection
        const { error } = await supabase
          .from('user_cards')
          .delete()
          .match({ card_id: cardId, user_id: user.id });

        if (error) throw error;

        setCollection(prev => {
          const newCollection = { ...prev };
          delete newCollection[cardId];
          return newCollection;
        });
      } else {
        const existingCard = collection[cardId];

        if (existingCard) {
          // Update existing card quantity
          const { error } = await supabase
            .from('user_cards')
            .update({ quantity })
            .match({ card_id: cardId, user_id: user.id });

          if (error) throw error;

          setCollection(prev => ({
            ...prev,
            [cardId]: { ...existingCard, quantity }
          }));
        } else {
          // Insert new card
          const { error } = await supabase
            .from('user_cards')
            .insert({ card_id: cardId, quantity, user_id: user.id, wanted: false, tradable: false });

          if (error) throw error;

          setCollection(prev => ({
            ...prev,
            [cardId]: { card_id: cardId, quantity, wanted: false, tradable: false }
          }));
        }
      }
    } catch (err) {
      console.error('Error updating collection:', err);
      setError(err instanceof Error ? err.message : 'Failed to update collection');
    }
  };

  const updateCardWanted = async (cardId: number, wanted: boolean) => {
    if (!user) return;

    try {
      const existingCard = collection[cardId];
      
      if (existingCard) {
        // Mise à jour d'une carte existante
        const { error } = await supabase
          .from('user_cards')
          .update({ wanted })
          .match({ card_id: cardId, user_id: user.id });

        if (error) throw error;
      } else {
        // Insertion d'une nouvelle carte
        const { error } = await supabase
          .from('user_cards')
          .insert({
            user_id: user.id,
            card_id: cardId,
            wanted,
            quantity: 0,
            tradable: false
          });

        if (error) throw error;
      }
      
      // Mettre à jour l'état local
      setCollection(prev => ({
        ...prev,
        [cardId]: {
          ...prev[cardId] || { card_id: cardId, quantity: 0, tradable: false },
          wanted
        }
      }));
    } catch (error) {
      console.error('Error updating card wanted status:', error);
      throw error;
    }
  };

  const updateCardTradable = async (cardId: number, tradable: boolean) => {
    if (!user) return;

    try {
      const existingCard = collection[cardId];
      
      if (existingCard) {
        // Mise à jour d'une carte existante
        const { error } = await supabase
          .from('user_cards')
          .update({ tradable })
          .match({ card_id: cardId, user_id: user.id });

        if (error) throw error;
      } else {
        // Insertion d'une nouvelle carte
        const { error } = await supabase
          .from('user_cards')
          .insert({
            user_id: user.id,
            card_id: cardId,
            tradable,
            quantity: 0,
            wanted: false
          });

        if (error) throw error;
      }
      
      // Mettre à jour l'état local
      setCollection(prev => ({
        ...prev,
        [cardId]: {
          ...prev[cardId] || { card_id: cardId, quantity: 0, wanted: false },
          tradable
        }
      }));
    } catch (error) {
      console.error('Error updating card tradable status:', error);
      throw error;
    }
  };

  return {
    collection,
    loading,
    error,
    updateCardQuantity,
    updateCardWanted,
    updateCardTradable,
    setCollection
  };
}